import React, { useState, useEffect, useCallback, useRef } from "react";
import useAxiosInstance from "../../hooks/useAxios.jsx";
import { toast } from "react-toastify";
import { FaCheckCircle, FaRegCircle, FaSpinner } from "react-icons/fa";
import { MdSave, MdClose, MdSearch, MdRemove } from "react-icons/md";

export default function ArticleGroups() {
  const axiosInstance = useAxiosInstance();
  const axiosRef = useRef(axiosInstance);
  axiosRef.current = axiosInstance;

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
  const groupsPageRef = useRef(1);
  const groupsTotalPagesRef = useRef(1);
  const isFetchingMoreRef = useRef(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const debounceRef = useRef(null);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [articles, setArticles] = useState([]);
  const [verifiedIds, setVerifiedIds] = useState(new Set());
  const [originalVerifiedIds, setOriginalVerifiedIds] = useState(new Set());
  const [invalidIds, setInvalidIds] = useState(new Set());
  const [originalInvalidIds, setOriginalInvalidIds] = useState(new Set());
  const [unsetIds, setUnsetIds] = useState(new Set());
  const [originalUnsetIds, setOriginalUnsetIds] = useState(new Set());
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingMoreArticles, setLoadingMoreArticles] = useState(false);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPageRef = useRef(1);
  const articlesTotalPagesRef = useRef(1);
  const isFetchingMoreArticlesRef = useRef(false);
  const selectedGroupRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);


  const fetchGroups = useCallback(async (page = 1, q = searchRef.current) => {
    if (isFetchingMoreRef.current) return;
    isFetchingMoreRef.current = true;
    page === 1 ? setLoadingGroups(true) : setLoadingMoreGroups(true);
    try {
      const params = { page, page_size: 20 };
      if (q) params.search = q;
      const res = await axiosRef.current.get("/sandbox/groups/", { params });
      const data = res.data;
      groupsTotalPagesRef.current = data.num_pages || 1;
      groupsPageRef.current = page;
      setGroups((prev) =>
        page === 1 ? data.results || [] : [...prev, ...(data.results || [])]
      );
    } catch {
      toast.error("Failed to load article groups");
    } finally {
      setLoadingGroups(false);
      setLoadingMoreGroups(false);
      isFetchingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchGroups(1);
  }, [fetchGroups]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    searchRef.current = q;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchGroups(1, q);
    }, 400);
  };

  const handleLoadMore = () => {
    fetchGroups(groupsPageRef.current + 1);
  };

  const fetchArticles = useCallback(async (groupId, page = 1) => {
    if (isFetchingMoreArticlesRef.current) return;
    isFetchingMoreArticlesRef.current = true;
    if (page === 1) {
      setLoadingArticles(true);
      setArticles([]);
      setVerifiedIds(new Set());
      setOriginalVerifiedIds(new Set());
      setInvalidIds(new Set());
      setOriginalInvalidIds(new Set());
      setUnsetIds(new Set());
      setOriginalUnsetIds(new Set());
      setIsDirty(false);
      selectedGroupRef.current = groupId;
    } else {
      setLoadingMoreArticles(true);
    }
    try {
      const res = await axiosRef.current.get(
        `/sandbox/groups/${groupId}/articles`,
        { params: { page, page_size: 20 } }
      );
      if (selectedGroupRef.current !== groupId) return;
      const data = res.data?.articles || [];
      articlesTotalPagesRef.current = res.data?.num_pages || 1;
      setTotalArticles(res.data?.total || 0);
      articlesPageRef.current = page;
      setArticles((prev) => page === 1 ? data : [...prev, ...data]);
      setVerifiedIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation?.is_valid === true).forEach((a) => n.add(a.article_id));
        return n;
      });
      setOriginalVerifiedIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation?.is_valid === true).forEach((a) => n.add(a.article_id));
        return n;
      });
      setInvalidIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation?.is_valid === false).forEach((a) => n.add(a.article_id));
        return n;
      });
      setOriginalInvalidIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation?.is_valid === false).forEach((a) => n.add(a.article_id));
        return n;
      });
      setUnsetIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation == null || a.validation.is_valid == null).forEach((a) => n.add(a.article_id));
        return n;
      });
      setOriginalUnsetIds((prev) => {
        const n = new Set(prev);
        data.filter((a) => a.validation == null || a.validation.is_valid == null).forEach((a) => n.add(a.article_id));
        return n;
      });
    } catch {
      toast.error("Failed to load articles");
    } finally {
      setLoadingArticles(false);
      setLoadingMoreArticles(false);
      isFetchingMoreArticlesRef.current = false;
    }
  }, []);

  const handleSelectGroup = (group) => {
    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Discard and switch groups?"
      );
      if (!confirmed) return;
    }
    setSelectedGroup(group);
    fetchArticles(group.group_id, 1);
  };

  const handleLoadMoreArticles = () => {
    if (selectedGroup) fetchArticles(selectedGroup.group_id, articlesPageRef.current + 1);
  };

  const toggleVerified = (articleId) => {
    const isVerified = verifiedIds.has(articleId);
    // cycle: unset/invalid → verified → invalid
    setUnsetIds((prev) => { const n = new Set(prev); n.delete(articleId); return n; });
    if (!isVerified) {
      setVerifiedIds((prev) => { const n = new Set(prev); n.add(articleId); return n; });
      setInvalidIds((prev) => { const n = new Set(prev); n.delete(articleId); return n; });
    } else {
      setVerifiedIds((prev) => { const n = new Set(prev); n.delete(articleId); return n; });
      setInvalidIds((prev) => { const n = new Set(prev); n.add(articleId); return n; });
    }
    setIsDirty(true);
  };

  const handleDiscard = () => {
    setVerifiedIds(new Set(originalVerifiedIds));
    setInvalidIds(new Set(originalInvalidIds));
    setUnsetIds(new Set(originalUnsetIds));
    setIsDirty(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = articles.map((a) => ({
        article_id: a.article_id,
        valid: verifiedIds.has(a.article_id),
        group_id: selectedGroup.group_id,
      }));
      await axiosRef.current.post("/sandbox/articles/validate/", payload);
      setOriginalVerifiedIds(new Set(verifiedIds));
      setIsDirty(false);
      toast.success("Changes saved successfully");
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-1 min-h-0 bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
      {/* Sidebar */}
      <aside className="w-72 min-w-[260px] bg-white border-r border-slate-200 flex flex-col overflow-hidden min-h-0">
        <div className="px-4 h-[56px] flex items-center border-b border-slate-200 shrink-0">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-slate-700">
            Article Groups
          </h2>
        </div>

        <div className="px-3 py-2 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
            <MdSearch className="text-slate-400 text-base shrink-0" />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search groups…"
              className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none w-full"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingGroups ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="text-xl animate-spin text-slate-400" />
            </div>
          ) : groups.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-400">No groups found</p>
          ) : (
            <>
              <ul>
                {groups.map((group) => {
                  const isActive =
                    selectedGroup?.group_id === group.group_id;
                  return (
                    <li key={group.group_id}>
                      <button
                        onClick={() => handleSelectGroup(group)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
                          isActive
                            ? "bg-slate-900 text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <p
                          className={`text-sm font-medium ${
                            isActive ? "text-white" : "text-slate-800"
                          }`}
                        >
                          Group #{group.group_id}
                        </p>
                        <p className={`text-xs mt-0.5 ${isActive ? "text-slate-300" : "text-slate-400"}`}>
                          {group.article_count != null ? `${group.article_count} article${group.article_count !== 1 ? "s" : ""}` : "No articles"}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {groupsPageRef.current < groupsTotalPagesRef.current && (
                <div className="p-3">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMoreGroups}
                    className="w-full py-2 text-xs text-slate-500 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {loadingMoreGroups ? (
                      <><FaSpinner className="animate-spin" /> Loading…</>
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex flex-col flex-1 overflow-hidden min-h-0">
        {/* Top action bar */}
        <div className="flex items-center justify-between px-6 bg-white border-b border-slate-200 h-[56px] shrink-0">
          <div>
            {selectedGroup && (
              <h1 className="text-base font-semibold text-slate-800">
                Group #{selectedGroup.group_id}
                {totalArticles > 0 && (
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    · {totalArticles} article{totalArticles !== 1 ? "s" : ""}
                  </span>
                )}
              </h1>
            )}
          </div>
          {isDirty && (
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button
                onClick={handleDiscard}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <MdClose className="text-base" />
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                {saving ? (
                  <FaSpinner className="text-sm animate-spin" />
                ) : (
                  <MdSave className="text-base" />
                )}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Articles grid */}
        <div className="flex-1 p-6 overflow-y-auto">
          {!selectedGroup ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <span className="mb-3 text-5xl">📂</span>
              <p className="text-sm">Select a group from the sidebar</p>
            </div>
          ) : loadingArticles ? (
            <div className="flex items-center justify-center h-full">
              <FaSpinner className="text-2xl animate-spin text-slate-400" />
            </div>
          ) : articles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <p className="text-sm">No articles in this group</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {articles.map((article) => {
                  const isVerified = verifiedIds.has(article.article_id);
                  const isInvalid = invalidIds.has(article.article_id);
                  const isUnset = unsetIds.has(article.article_id);
                  return (
                    <ArticleCard
                      key={article.article_id}
                      article={article}
                      isVerified={isVerified}
                      isInvalid={isInvalid}
                      isUnset={isUnset}
                      onToggle={() => toggleVerified(article.article_id)}
                    />
                  );
                })}
              </div>
              {articlesPageRef.current < articlesTotalPagesRef.current && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleLoadMoreArticles}
                    disabled={loadingMoreArticles}
                    className="px-4 py-2 text-xs text-slate-500 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  >
                    {loadingMoreArticles ? (
                      <><FaSpinner className="animate-spin" /> Loading…</>
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function ArticleCard({ article, isVerified, isInvalid, isUnset, onToggle }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <div
        className={`bg-white rounded-lg border flex flex-col transition-all ${
          isVerified
            ? "border-green-400 shadow-sm shadow-green-100"
            : isInvalid
            ? "border-red-400 shadow-sm shadow-red-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <div className="p-4 flex flex-col gap-2 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-slate-800 leading-snug line-clamp-3 flex-1">
              {article.title || `Article #${article.article_id}`}
            </h3>
            <button
              onClick={onToggle}
              className="shrink-0 mt-0.5 transition-colors"
              title={isVerified ? "Mark as unverified" : "Mark as verified"}
            >
              {isVerified ? (
                <FaCheckCircle className="text-xl text-green-500" />
              ) : isInvalid ? (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500">
                  <MdClose className="text-white text-sm" />
                </span>
              ) : isUnset ? (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-400">
                  <MdRemove className="text-white text-sm" />
                </span>
              ) : (
                <FaRegCircle className="text-xl text-slate-300 hover:text-slate-400" />
              )}
            </button>
          </div>

          {article.ai_summary && (
            <p className="text-xs text-slate-500 line-clamp-3 leading-relaxed">
              {article.ai_summary}
            </p>
          )}

          <div className="mt-auto pt-2 flex flex-col gap-1">
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-500 hover:underline truncate"
              >
                {article.url}
              </a>
            )}
            {article.published_at && (
              <p className="text-xs text-slate-400">
                {new Date(article.published_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
          </div>

          <button
            onClick={() => setModalOpen(true)}
            className="mt-1 text-xs text-slate-400 hover:text-slate-600 text-left underline underline-offset-2 transition-colors"
          >
            Show more
          </button>
        </div>
      </div>

      {modalOpen && (
        <ArticleModal
          article={article}
          isVerified={isVerified}
          isInvalid={isInvalid}
          isUnset={isUnset}
          onToggle={onToggle}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

function ArticleModal({ article, isVerified, isInvalid, isUnset, onToggle, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-800 leading-snug flex-1">
            {article.title || `Article #${article.article_id}`}
          </h2>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onToggle}
              className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                isVerified ? "text-green-500" : isInvalid ? "text-red-500" : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {isVerified ? (
                <><FaCheckCircle className="text-base" /> Verified</>
              ) : isInvalid ? (
                <>
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-red-500">
                    <MdClose className="text-white text-[9px]" />
                  </span>
                  Invalid
                </>
              ) : isUnset ? (
                <>
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-orange-400">
                    <MdRemove className="text-white text-[9px]" />
                  </span>
                  Unset
                </>
              ) : (
                <><FaRegCircle className="text-base" /> Mark verified</>
              )}
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <div className="overflow-y-auto p-6 flex flex-col gap-4">
          {article.ai_summary && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                AI Summary
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {article.ai_summary}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5 text-xs text-slate-500">
            {article.url && (
              <div className="flex gap-2">
                <span className="font-medium text-slate-400 w-20 shrink-0">URL</span>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 hover:underline break-all"
                >
                  {article.url}
                </a>
              </div>
            )}
            {article.published_at && (
              <div className="flex gap-2">
                <span className="font-medium text-slate-400 w-20 shrink-0">Published</span>
                <span>
                  {new Date(article.published_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            )}
            {article.validation?.verified_at && (
              <div className="flex gap-2">
                <span className="font-medium text-slate-400 w-20 shrink-0">Verified at</span>
                <span>
                  {new Date(article.validation.verified_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
